import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../store/auth';

type Agent = { id: string; name: string; email: string; role: string; isActive: boolean; isOnline: boolean; totalTickets: number; csatAverage: number; skills: string[]; maxOpenTickets: number };

export default function AgentsPage() {
  const actor = useAuth((s) => s.actor);
  const canCreate = actor?.type === 'OWNER' || actor?.type === 'SUPER_ADMIN';
  const qc = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);

  const { data: agents = [] } = useQuery({
    queryKey: ['agents'],
    queryFn: async () => (await api.get('/agents')).data as Agent[],
  });

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Agents</h1>
          <p className="text-sm text-slate-500">{agents.length} active</p>
        </div>
        {canCreate && <button className="btn-primary" onClick={() => setShowAdd(true)}>+ Add agent</button>}
      </div>

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Role</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Capacity</th>
              <th className="text-left px-4 py-3">CSAT</th>
              <th className="text-left px-4 py-3">Skills</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {agents.map((a) => (
              <tr key={a.id}>
                <td className="px-4 py-3 font-medium">{a.name}</td>
                <td className="px-4 py-3 text-slate-600">{a.email}</td>
                <td className="px-4 py-3"><span className="badge bg-slate-100 text-slate-700">{a.role}</span></td>
                <td className="px-4 py-3">
                  <span className={'badge ' + (a.isOnline ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600')}>{a.isOnline ? 'online' : 'offline'}</span>
                  {!a.isActive && <span className="badge bg-rose-100 text-rose-700 ml-2">inactive</span>}
                </td>
                <td className="px-4 py-3 text-slate-600">{a.maxOpenTickets}</td>
                <td className="px-4 py-3 text-slate-600">{a.csatAverage ? a.csatAverage.toFixed(2) : '—'}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{a.skills.join(', ') || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAdd && <AddAgentModal onClose={() => setShowAdd(false)} onAdded={() => { setShowAdd(false); qc.invalidateQueries({ queryKey: ['agents'] }); }} />}
    </div>
  );
}

function AddAgentModal({ onClose, onAdded }: { onClose: () => void; onAdded: () => void }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  // No default password — every agent used to be created with the same
  // well-known 'agent@123' unless the admin remembered to overwrite it.
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('AGENT');
  const create = useMutation({
    mutationFn: async () => api.post('/agents', { name, email, password, role }),
    onSuccess: () => { toast.success('Agent created'); onAdded(); },
    onError: (e: { response?: { data?: { error?: string } } }) => toast.error(e.response?.data?.error ?? 'Failed'),
  });
  return (
    <div className="fixed inset-0 bg-slate-900/40 grid place-items-center p-4 z-50">
      <div className="card w-full max-w-md p-6">
        <h2 className="text-lg font-semibold mb-4">Add agent</h2>
        <div className="space-y-3">
          <div><label className="label">Name</label><input className="input" value={name} onChange={(e) => setName(e.target.value)} /></div>
          <div><label className="label">Email</label><input className="input" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
          <div><label className="label">Password</label><input type="password" className="input" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" /></div>
          <div><label className="label">Role</label>
            <select className="input" value={role} onChange={(e) => setRole(e.target.value)}>
              {['ADMIN', 'SUPERVISOR', 'AGENT', 'READONLY'].map((r) => <option key={r}>{r}</option>)}
            </select>
          </div>
        </div>
        <div className="flex gap-2 justify-end mt-5">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={!name || !email || !password || create.isPending} onClick={() => create.mutate()}>{create.isPending ? 'Adding…' : 'Add agent'}</button>
        </div>
      </div>
    </div>
  );
}
